/**
 * Notification Templates Page (US-16-GR-23)
 *
 * List / filter / create / edit / deactivate notification templates via
 * /api/Notifications/Templates. Client-side token validation mirrors the
 * server's unknown-token rejection so admins get inline feedback before saving.
 */

import { useCallback, useEffect, useRef, useState, startTransition, type FormEvent } from 'react';
import { RefreshCw, AlertCircle, Bell, Plus, Edit2, PowerOff, X, CheckCircle } from 'lucide-react';
import {
  listTemplates,
  createTemplate,
  updateTemplate,
  deactivateTemplate,
  findUnknownTokens,
  UnknownTokenError,
  TemplateCategory,
  TemplateChannel,
  TEMPLATE_CATEGORY_LABELS,
  TEMPLATE_CHANNEL_LABELS,
  type NotificationTemplate,
  type TemplateInput,
} from '../services/notificationTemplatesService';

const CATEGORY_OPTIONS = Object.values(TemplateCategory).filter(
  (v): v is TemplateCategory => typeof v === 'number',
);
const CHANNEL_OPTIONS = Object.values(TemplateChannel).filter(
  (v): v is TemplateChannel => typeof v === 'number',
);

const CHANNEL_COLOR: Record<TemplateChannel, string> = {
  [TemplateChannel.Email]: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  [TemplateChannel.InApp]: 'text-violet-400 bg-violet-500/10 border-violet-500/20',
  [TemplateChannel.SMS]: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
};

// ---------------------------------------------------------------------------
// Template form (create / edit)
// ---------------------------------------------------------------------------

interface TemplateFormProps {
  initial?: Partial<TemplateInput>;
  onSave: (req: TemplateInput) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
}

function TemplateForm({ initial, onSave, onCancel, saving }: TemplateFormProps) {
  const [category, setCategory] = useState<TemplateCategory>(
    initial?.category ?? TemplateCategory.TaskDueReminder,
  );
  const [channel, setChannel] = useState<TemplateChannel>(initial?.channel ?? TemplateChannel.InApp);
  const [subject, setSubject] = useState(initial?.subject ?? '');
  const [body, setBody] = useState(initial?.bodyTemplate ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);

  // Live client-side token validation mirrors the server's unknown-token check.
  const unknownTokens = findUnknownTokens(body);
  const hasUnknownTokens = unknownTokens.length > 0;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (hasUnknownTokens) return;
    await onSave({
      category,
      channel,
      subject: channel === TemplateChannel.Email ? subject || null : null,
      bodyTemplate: body,
      isActive,
      isAIGhostwritingScaffold: initial?.isAIGhostwritingScaffold ?? false,
    });
  };

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="space-y-4 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Category */}
        <div>
          <label htmlFor="tmpl-category" className="block text-xs font-medium text-zinc-400 mb-1">
            Category *
          </label>
          <select
            id="tmpl-category"
            value={category}
            onChange={(e) => setCategory(Number(e.target.value) as TemplateCategory)}
            className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500"
          >
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {TEMPLATE_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </div>

        {/* Channel */}
        <div>
          <label htmlFor="tmpl-channel" className="block text-xs font-medium text-zinc-400 mb-1">
            Channel *
          </label>
          <select
            id="tmpl-channel"
            value={channel}
            onChange={(e) => setChannel(Number(e.target.value) as TemplateChannel)}
            className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500"
          >
            {CHANNEL_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {TEMPLATE_CHANNEL_LABELS[c]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Subject (Email only) */}
      <div>
        <label htmlFor="tmpl-subject" className="block text-xs font-medium text-zinc-400 mb-1">
          Subject <span className="text-zinc-600">(Email only)</span>
        </label>
        <input
          id="tmpl-subject"
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={channel !== TemplateChannel.Email}
          className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 disabled:opacity-40"
          placeholder="e.g. Your task is due soon"
        />
      </div>

      {/* Body */}
      <div>
        <label htmlFor="tmpl-body" className="block text-xs font-medium text-zinc-400 mb-1">
          Body Template *
        </label>
        <textarea
          id="tmpl-body"
          required
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          aria-invalid={hasUnknownTokens}
          className={`w-full px-3 py-2 rounded-lg bg-zinc-900 border text-sm text-zinc-200 font-mono focus:outline-none focus:ring-2 resize-y ${
            hasUnknownTokens
              ? 'border-red-500/50 focus:ring-red-500/40'
              : 'border-zinc-700 focus:ring-violet-500/50 focus:border-violet-500'
          }`}
          placeholder="Use {{userName}}, {{taskTitle}}, etc. for placeholders."
        />
        {hasUnknownTokens ? (
          <p className="text-xs text-red-400 mt-1 flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5" />
            Unknown placeholder tokens: {unknownTokens.map((t) => `{{${t}}}`).join(', ')}
          </p>
        ) : (
          <p className="text-xs text-zinc-500 mt-1">
            Placeholders use {'{{token}}'} syntax. Unknown tokens are rejected on save.
          </p>
        )}
      </div>

      {/* Active toggle */}
      <div className="flex items-center gap-2">
        <input
          id="tmpl-active"
          type="checkbox"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          className="w-4 h-4 accent-violet-500"
        />
        <label htmlFor="tmpl-active" className="text-xs text-zinc-400">
          Active
        </label>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={saving || hasUnknownTokens}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-zinc-950 text-sm font-medium hover:bg-violet-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]"
        >
          {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
          {saving ? 'Saving…' : 'Save Template'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg bg-zinc-700 text-zinc-300 text-sm font-medium hover:bg-zinc-600 transition-colors min-h-[48px]"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Template row
// ---------------------------------------------------------------------------

function TemplateRow({
  template,
  onEdit,
  onDeactivate,
}: {
  template: NotificationTemplate;
  onEdit: (t: NotificationTemplate) => void;
  onDeactivate: (id: string) => Promise<void>;
}) {
  const [deactivating, setDeactivating] = useState(false);

  const handleDeactivate = async () => {
    setDeactivating(true);
    try {
      await onDeactivate(template.id);
    } finally {
      setDeactivating(false);
    }
  };

  return (
    <div
      className={`flex items-start gap-4 p-4 rounded-lg border ${
        template.isActive
          ? 'border-zinc-700 bg-zinc-800/30'
          : 'border-zinc-800 bg-zinc-900/50 opacity-60'
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-zinc-200">
            {TEMPLATE_CATEGORY_LABELS[template.category] ?? `Category ${template.category}`}
          </span>
          <span
            className={`px-1.5 py-0.5 text-xs rounded border ${
              CHANNEL_COLOR[template.channel] ?? 'text-zinc-400 bg-zinc-800 border-zinc-700'
            }`}
          >
            {TEMPLATE_CHANNEL_LABELS[template.channel] ?? `Channel ${template.channel}`}
          </span>
          {!template.isActive && (
            <span className="px-1.5 py-0.5 text-xs rounded border border-zinc-800 text-zinc-600">
              Inactive
            </span>
          )}
        </div>
        {template.subject && (
          <p className="text-xs text-zinc-400 mt-1 truncate">Subject: {template.subject}</p>
        )}
        <p className="text-xs text-zinc-500 mt-1 truncate font-mono">{template.bodyTemplate}</p>
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          type="button"
          onClick={() => onEdit(template)}
          className="p-1.5 text-zinc-400 hover:text-zinc-200 transition-colors rounded-lg hover:bg-zinc-700"
          aria-label={`Edit ${TEMPLATE_CATEGORY_LABELS[template.category] ?? 'template'}`}
        >
          <Edit2 className="w-4 h-4" />
        </button>
        {template.isActive && (
          <button
            type="button"
            onClick={() => void handleDeactivate()}
            disabled={deactivating}
            className="p-1.5 text-zinc-400 hover:text-red-400 transition-colors rounded-lg hover:bg-zinc-700 disabled:opacity-50"
            aria-label={`Deactivate ${TEMPLATE_CATEGORY_LABELS[template.category] ?? 'template'}`}
          >
            {deactivating ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <PowerOff className="w-4 h-4" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function NotificationTemplatesPage() {
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<NotificationTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  // US-19-GR-81: captured so it can be cleared on unmount, instead of a bare setTimeout.
  const savedMsgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (savedMsgTimerRef.current) clearTimeout(savedMsgTimerRef.current);
  }, []);

  // Filters
  const [filterCategory, setFilterCategory] = useState<TemplateCategory | ''>('');
  const [filterChannel, setFilterChannel] = useState<TemplateChannel | ''>('');
  const [filterActive, setFilterActive] = useState<'' | 'true' | 'false'>('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listTemplates({
        category: filterCategory === '' ? undefined : filterCategory,
        channel: filterChannel === '' ? undefined : filterChannel,
        isActive: filterActive === '' ? undefined : filterActive === 'true',
      });
      setTemplates(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterChannel, filterActive]);

  useEffect(() => {
    startTransition(() => {
      void load();
    });
  }, [load]);

  const surfaceError = (err: unknown, fallback: string) => {
    if (err instanceof UnknownTokenError) {
      setError(err.message);
    } else {
      setError(err instanceof Error ? err.message : fallback);
    }
  };

  const handleCreate = async (req: TemplateInput) => {
    setSaving(true);
    setError(null);
    try {
      const created = await createTemplate(req);
      setTemplates((prev) => [created, ...prev]);
      setShowForm(false);
      setSavedMsg('Template created');
      savedMsgTimerRef.current = setTimeout(() => setSavedMsg(null), 3000);
    } catch (err) {
      surfaceError(err, 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (req: TemplateInput) => {
    if (!editTarget) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateTemplate(editTarget.id, req);
      setTemplates((prev) => prev.map((t) => (t.id === editTarget.id ? updated : t)));
      setEditTarget(null);
      setSavedMsg('Template updated');
      savedMsgTimerRef.current = setTimeout(() => setSavedMsg(null), 3000);
    } catch (err) {
      surfaceError(err, 'Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    try {
      await deactivateTemplate(id);
      setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, isActive: false } : t)));
    } catch (err) {
      surfaceError(err, 'Failed to deactivate template');
    }
  };

  const selectClass =
    'px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500';

  return (
    <main className="flex-1 p-6 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-amber-400" />
          <h1 className="text-lg font-semibold text-zinc-100">Notification Templates</h1>
          {!loading && <span className="text-xs text-zinc-500">({templates.length})</span>}
        </div>

        <div className="flex items-center gap-2">
          {savedMsg && (
            <span className="flex items-center gap-1.5 text-xs text-emerald-400">
              <CheckCircle className="w-3.5 h-3.5" />
              {savedMsg}
            </span>
          )}
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="p-1.5 text-zinc-400 hover:text-zinc-200 transition-colors rounded-lg hover:bg-zinc-800"
            aria-label="Refresh templates"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => {
              setEditTarget(null);
              setShowForm(true);
            }}
            disabled={showForm || !!editTarget}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-violet-600 text-zinc-950 hover:bg-violet-500 transition-colors disabled:opacity-50 min-h-[48px]"
          >
            <Plus className="w-3.5 h-3.5" />
            New Template
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          aria-label="Filter by category"
          value={filterCategory}
          onChange={(e) =>
            setFilterCategory(e.target.value === '' ? '' : (Number(e.target.value) as TemplateCategory))
          }
          className={selectClass}
        >
          <option value="">All categories</option>
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {TEMPLATE_CATEGORY_LABELS[c]}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter by channel"
          value={filterChannel}
          onChange={(e) =>
            setFilterChannel(e.target.value === '' ? '' : (Number(e.target.value) as TemplateChannel))
          }
          className={selectClass}
        >
          <option value="">All channels</option>
          {CHANNEL_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {TEMPLATE_CHANNEL_LABELS[c]}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter by active state"
          value={filterActive}
          onChange={(e) => setFilterActive(e.target.value as '' | 'true' | 'false')}
          className={selectClass}
        >
          <option value="">Active & inactive</option>
          <option value="true">Active only</option>
          <option value="false">Inactive only</option>
        </select>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-3 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-400 flex-1">{error}</p>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-zinc-500 hover:text-zinc-300"
            aria-label="Dismiss error"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Create form */}
      {showForm && !editTarget && (
        <div className="mb-4">
          <TemplateForm onSave={handleCreate} onCancel={() => setShowForm(false)} saving={saving} />
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw className="w-5 h-5 text-zinc-500 animate-spin" />
        </div>
      )}

      {/* Empty */}
      {!loading && templates.length === 0 && !showForm && (
        <div className="text-center py-10 border border-zinc-800 rounded-lg">
          <Bell className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">No notification templates match the current filters.</p>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="mt-3 text-xs text-violet-400 hover:text-violet-300 transition-colors"
          >
            Create a template
          </button>
        </div>
      )}

      {/* List */}
      {!loading && templates.length > 0 && (
        <div className="space-y-2">
          {templates.map((t) =>
            editTarget?.id === t.id ? (
              <TemplateForm
                key={t.id}
                initial={{
                  category: t.category,
                  channel: t.channel,
                  subject: t.subject,
                  bodyTemplate: t.bodyTemplate,
                  isActive: t.isActive,
                  isAIGhostwritingScaffold: t.isAIGhostwritingScaffold,
                }}
                onSave={handleUpdate}
                onCancel={() => setEditTarget(null)}
                saving={saving}
              />
            ) : (
              <TemplateRow
                key={t.id}
                template={t}
                onEdit={setEditTarget}
                onDeactivate={handleDeactivate}
              />
            ),
          )}
        </div>
      )}
    </main>
  );
}

export default NotificationTemplatesPage;
